import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useMediaQuery, } from "react-responsive";

const CHART_H = 400;
const PAD_L = 56;
const PAD_R = 16;
const PAD_T = 31;
const PAD_B = 26;
const DOT_COLOR = '#003032';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const useCoordinateCalculators = (width, plotWidth, plotHeight,isMobile) => {
    const X_INSET = isMobile ? 30 : 70;

    const xOf = useCallback((i, dataLength) => {
    if (dataLength === 1) {
        return PAD_L + X_INSET + (plotWidth - X_INSET * 2) / 2;
    }
    return (
        PAD_L +
        X_INSET +
        (i / (dataLength - 1)) * (plotWidth - X_INSET * 2)
    );
    }, [plotWidth]);

    const yOf = useCallback((v, yMax) => 
        PAD_T + plotHeight - ((clamp(v, 0, yMax) / yMax) * plotHeight),
        [plotHeight]
    );

  return { xOf, yOf };
};


const usePointsArrays = (data, dataForLineDrawing, yOf, xOf, fullDataIndices, yMax, isManual, getValuePositionInZone, marker) => {
  const validDataPointsForLine = useMemo(
    () => dataForLineDrawing.filter(d => d.value !== null),
    [dataForLineDrawing]
  );

  const validDataPoints = useMemo(
    () => data.filter(d => d.value !== null),
    [data]
  );

  const monthGroups = useMemo(() => {
    const groups = {};
    validDataPoints.forEach((point, idx) => {
      const monthKey = point.monthKey || point.label;
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push({ ...point, originalIndex: idx });
    });
    
    Object.keys(groups).forEach(month => {
      groups[month].sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
    });
    
    return groups;
  }, [validDataPoints]);

  const pts = useMemo(() => {
      return validDataPoints.map((d) => {
        const visibleIndex = data.findIndex(point => 
          point.label === d.label && point.fullDate === d.fullDate
        );
        const fullIndex = fullDataIndices?.[visibleIndex] ?? visibleIndex;
        let yPosition;
        if (isManual && getValuePositionInZone && marker) {
          const positionedValue = getValuePositionInZone(marker);
          yPosition = yOf(positionedValue, yMax);
        } else {
          yPosition = yOf(d.value, yMax);
        }
        
        let xPosition = xOf(visibleIndex, data.length);
        
        const monthKey = d.monthKey || d.label;
        const pointsInMonth = monthGroups[monthKey] || [];
        const pointIndex = pointsInMonth.findIndex(p => p.fullDate === d.fullDate);
        const totalPoints = pointsInMonth.length;
        
        if (totalPoints > 1) {
          const offset = (pointIndex - (totalPoints - 1) / 2);
          xPosition = xPosition + offset;
        }
        
        return { 
          x: xPosition, 
          y: yPosition, 
          visibleIndex, 
          fullIndex, 
          dataIndex: visibleIndex,
          pointIndex: pointIndex,
          totalPoints: totalPoints
        };
      });
    }, [validDataPoints, data, xOf, yOf, fullDataIndices, yMax, isManual, getValuePositionInZone, marker, monthGroups]);

  const ptsForLine = useMemo(() => {
    return validDataPointsForLine.map((d) => {
      const lineDataIndex = dataForLineDrawing.findIndex(point => 
        point.label === d.label && point.fullDate === d.fullDate
      );
      const visibleIndex = data.findIndex(point => 
        point.label === d.label && point.fullDate === d.fullDate
      );
      const indexForX = visibleIndex !== -1 ? visibleIndex : lineDataIndex;
      
      let yPosition;
      if (isManual && getValuePositionInZone && marker) {
        const positionedValue = getValuePositionInZone(marker);
        yPosition = yOf(positionedValue, yMax);
      } else {
        yPosition = yOf(d.value, yMax);
      }
      
      let xPosition = xOf(indexForX, data.length);
      
      const monthKey = d.monthKey || d.label;
      const pointsInMonth = monthGroups[monthKey] || [];
      const pointIndex = pointsInMonth.findIndex(p => p.fullDate === d.fullDate);
      const totalPoints = pointsInMonth.length;
      
      if (totalPoints > 1) {
        const offset = (pointIndex - (totalPoints - 1) / 2);
        xPosition = xPosition + offset;
      }
      
      return { 
        x: xPosition, 
        y: yPosition,
        dataIndex: lineDataIndex,
        visibleIndex: indexForX,
        originalIndex: d.originalIndex
      };
    });
  }, [validDataPointsForLine, dataForLineDrawing, data, xOf, yOf, yMax, isManual, getValuePositionInZone, marker, monthGroups]);

  return { pts, ptsForLine };
};

const extendLineToEdges = (pts, data, PAD_L,hasPreviousData) => {
  if (pts.length === 0) return [];
  
  const sortedPts = [...pts].sort((a, b) => a.visibleIndex - b.visibleIndex);
  const firstVisiblePoint = sortedPts[0];
  const hasDataAtStart = hasPreviousData;
  
  const extendedPts = [];
  
  if (hasDataAtStart) {
    extendedPts.push({ ...firstVisiblePoint, x: PAD_L, isExtension: true });
  }
  
  extendedPts.push(...sortedPts);
  
  return extendedPts;
};

const useLinePathBuilder = (ptsForLine, data, ignoreGaps, globalData, PAD_L,hasPreviousData) => {
  return useMemo(() => {
    if (ptsForLine.length < 2) return '';
    
    const extendedPts = extendLineToEdges(ptsForLine, data, PAD_L,hasPreviousData);
    
    return extendedPts
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' ');
      
  }, [ptsForLine, data, PAD_L]);
};

const usePointHandlers = (onHoverPoint, onPointClick) => {
  const handleMouseEnter = useCallback((point, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    onHoverPoint(point.fullIndex, {
      x: point.x,
      y: point.y,
      clientX: rect.left + rect.width / 2,
      clientY: rect.top
    });
  }, [onHoverPoint]);

  const handleMouseLeave = useCallback(() => {
    onHoverPoint(null, null);
  }, [onHoverPoint]);

  const handlePointClick = useCallback((visibleIndex) => {
    onPointClick(visibleIndex);
  }, [onPointClick]);

  return { handleMouseEnter, handleMouseLeave, handlePointClick };
};


const useOptimizedYTicks = (yMax, yTicks) => {
  return useMemo(() => {

    if (yTicks && yTicks.length > 0 && isFinite(yMax)) {
      return yTicks;
    }

    const ticks = [];
    const step = Math.pow(10, Math.floor(Math.log10(yMax)) - 1);
    let tick = 0;

    while (tick <= yMax) {
      ticks.push(tick);
      tick += step;
    }

    if (ticks.length < 3) {
      const newStep = yMax / 5;
      const newTicks = [];
      for (let i = 0; i <= 5; i++) {
        newTicks.push(Math.round((i * newStep) * 100) / 100);
      }
      return newTicks;
    }

    return ticks;
  }, [yMax, yTicks]);
};

const ZoneBands = React.memo(({ 
  zoneBands, 
  activeCat, 
  yOf, 
  yMax, 
  PAD_L, 
  plotWidth 
}) => {
  return (
    <>
      {zoneBands.map(band => {
        const isActive = band.key === activeCat.key;
        const bandYTop = yOf(Math.min(band.yHi, yMax), yMax);
        const bandYBot = yOf(Math.max(band.yLo, 0), yMax);
        const bandH = Math.max(0, bandYBot - bandYTop);
        
        return (
          <rect
            key={band.key}
            x={PAD_L}
            y={bandYTop}
            width={plotWidth}
            height={bandH}
            fill={band.zoneFill}
            opacity={isActive ? 1 : 0}
            clipPath="url(#mgClip)"
            style={{ transition: 'opacity 0.4s ease' }}
          />
        );
      })}

      {zoneBands.map((band) => {
        const bandYTop = yOf(Math.min(band.yHi, yMax), yMax);
        const bandYBot = yOf(Math.max(band.yLo, 0), yMax);
        return (
          <rect
            key={`${band.key}-accent`}
            x={PAD_L}
            y={bandYTop}
            width={4}
            height={Math.max(0, bandYBot - bandYTop)}
            fill={band.dotColor}
            opacity={0.7}
            clipPath="url(#mgClip)"
            style={{ transition: 'fill 0.4s ease' }}
          />
        );
      })}
    </>
  );
});

ZoneBands.displayName = 'ZoneBands';

const GridLines = React.memo(({ 
  yTicks, 
  yOf, 
  yMax, 
  PAD_L, 
  PAD_T,
  plotWidth, 
  plotHeight,
  xLabels,
  xOf,
  showVerticalGrid = true,
  showHorizontalGrid = true,
  isManual,
  data
}) => {

  const monthPositions = useMemo(() => {
    const positions = [];
    const seenMonths = new Set();
    
    data.forEach((item, idx) => {
      if (!seenMonths.has(item.label)) {
        seenMonths.add(item.label);
        positions.push({
          label: item.label,
          x: xOf(idx, data.length),
          index: idx
        });
      }
    });
    
    return positions;
  }, [data, xOf]);

  return (
    <>
      {/* Horizontal grid lines and Y-axis labels */}
      {showHorizontalGrid && !isManual && yTicks.slice(0, -1).map(tick => {
        const ty = yOf(tick, yMax);
        return (
          <g key={`h-grid-${tick}`}>
            <line
              x1={PAD_L}
              y1={ty}
              x2={PAD_L + plotWidth}
              y2={ty}
              stroke="#ECECEE"
              strokeWidth={1}
              opacity={1}
            />
            <text 
              x={PAD_L - 4} 
              y={ty + 4} 
              fontSize={11} 
              fill="#BBB" 
              textAnchor="end"
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {tick}
            </text>
          </g>
        );
      })}

      {showVerticalGrid && monthPositions.map((month, i) => {
        return (
          <line
            key={`v-grid-${i}`}
            x1={month.x}
            y1={PAD_T}
            x2={month.x}
            y2={PAD_T + plotHeight}
            stroke="#ECECEE"
            strokeWidth={1}
            opacity={1}
          />
        );
      })}
    </>
  );
});

GridLines.displayName = 'GridLines';

const DataPoints = React.memo(({ 
  pts, 
  activeIndex, 
  hoveredIndex,
  onMouseEnter,
  onMouseLeave,
  onPointClick
}) => {
  return (
    <>
      {pts.map((point, idx) => {
        const isHovered = hoveredIndex === point.fullIndex;
        const isActive = activeIndex === point.visibleIndex;
        return (
          <g
            key={`point-${idx}`}
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => onMouseEnter(point, e)}
            onMouseLeave={onMouseLeave}
            onClick={() => onPointClick(point.visibleIndex)}
          >
            <circle
              cx={point.x}
              cy={point.y}
              r={9}
              fill={DOT_COLOR}
              opacity={0.15}
            />
            <circle
              cx={point.x}
              cy={point.y}
              r={4}
              fill={DOT_COLOR}
              stroke="white"
              strokeWidth={0}
              style={{ transition: 'r 0.2s ease, fill 0.2s ease' }}
            />
          </g>
        );
      })}
    </>
  );
});

DataPoints.displayName = 'DataPoints';

const XAxisLabels = React.memo(({ 
  xLabels, 
  data, 
  activeIndex, 
  xOf, 
  PAD_T, 
  plotHeight 
}) => {
  const monthPositions = useMemo(() => {
    const positions = {};
    const seenMonths = new Set();
    
    data.forEach((item, idx) => {
      const monthLabel = item.label;
      if (!seenMonths.has(monthLabel)) {
        seenMonths.add(monthLabel);
        positions[monthLabel] = idx;
      }
    });
    
    return positions;
  }, [data]);
  
  const uniqueMonths = useMemo(() => {
    return [...new Set(data.map(d => d.label))];
  }, [data]);
  
  return (
    <>
      {uniqueMonths.map((month, i) => {
        const originalIndex = monthPositions[month];
        const x = xOf(originalIndex, data.length);
        const activeMonth =
          activeIndex >= 0
            ? data[activeIndex]?.label
            : null;

        const isActive = month === activeMonth;
        const axisY = PAD_T + plotHeight;
        
        return (
          <g key={`month-label-${i}`}>
            <text
              x={x}
              y={axisY + 16}
              fontSize={12}
              fontWeight={isActive ? '700' : '400'}
              fill={isActive ? DOT_COLOR : '#AAA'}
              textAnchor="middle"
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {month}
            </text>
          </g>
        );
      })}
    </>
  );
});

XAxisLabels.displayName = 'XAxisLabels';

function LineChart({ 
  data, 
  activeIndex, 
  onPointClick, 
  activeCat, 
  yTicks, 
  yMax, 
  zoneBands, 
  xLabels, 
  onHoverPoint, 
  hoveredIndex, 
  fullDataIndices, 
  ignoreGaps = false, 
  fullData = null,
  globalData = null,
  grid = { vertical: true, horizontal: true },
  isManual = false,
  isMobileView,
  marker,
  getValuePositionInZone,
  lineData,
  hasPreviousData
}) {
  const [width, setWidth] = useState(600);
  const observerRef = useRef(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const measuredRef = useCallback(node => {
    if (!node) return;
    
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const resizeObserver = new ResizeObserver(entries => {
      setWidth(Math.floor(entries[0].contentRect.width));
    });
    resizeObserver.observe(node);
    observerRef.current = resizeObserver;

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const plotWidth = width - PAD_L - PAD_R;
  const plotHeight = isMobileView ? 380 - PAD_T - PAD_B : (isMobile ? 250 : CHART_H) - PAD_T - PAD_B;

  let dataForLineDrawing;
  if (ignoreGaps && fullData) {
    dataForLineDrawing = lineData;
  } else if (globalData) {
    dataForLineDrawing = globalData;
  } else {
    dataForLineDrawing = data;
  }


  const { xOf, yOf } = useCoordinateCalculators(width, plotWidth, plotHeight,isMobile);
  const { pts, ptsForLine } = usePointsArrays(data, dataForLineDrawing, yOf, xOf, fullDataIndices, yMax, isManual, getValuePositionInZone, marker);
  const lineD = useLinePathBuilder(ptsForLine, data, ignoreGaps, globalData, PAD_L,hasPreviousData);
  const { handleMouseEnter, handleMouseLeave, handlePointClick } = usePointHandlers(onHoverPoint, onPointClick);

  const optimizedYTicks = useOptimizedYTicks(yMax, yTicks);

  return (
    <div ref={measuredRef} style={{ width: '100%', position: 'relative' }}>
      <svg
        width="100%"
        height={isMobileView ? 380 : (isMobile ? 250 : CHART_H)}
        viewBox={`0 0 ${width} ${isMobileView ? 380: (isMobile ? 250 : CHART_H)}`}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="mgAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={DOT_COLOR} stopOpacity="0.14" />
            <stop offset="100%" stopColor={DOT_COLOR} stopOpacity="0" />
          </linearGradient>
          <clipPath id="mgClip">
            <rect x={PAD_L} y={PAD_T} width={plotWidth} height={plotHeight} />
          </clipPath>
        </defs>

        <GridLines
          yTicks={optimizedYTicks}
          yOf={yOf}
          yMax={yMax}
          PAD_L={PAD_L}
          PAD_T={PAD_T}
          plotWidth={plotWidth}
          plotHeight={plotHeight}
          xLabels={xLabels}
          xOf={xOf}
          showVerticalGrid={grid?.vertical !== false}
          showHorizontalGrid={grid?.horizontal !== false}
          isManual={isManual}
          data={data}
        />

        <ZoneBands 
          zoneBands={zoneBands}
          activeCat={activeCat}
          yOf={yOf}
          yMax={yMax}
          PAD_L={PAD_L}
          plotWidth={plotWidth}
        />

        <line
          x1={PAD_L}
          y1={PAD_T + plotHeight}
          x2={PAD_L + plotWidth}
          y2={PAD_T + plotHeight}
          stroke="#E0DDD4"
          strokeWidth={1}
        />

        <line
          x1={PAD_L}
          y1={PAD_T}
          x2={PAD_L}
          y2={PAD_T + plotHeight}
          stroke="#E0DDD4"
          strokeWidth={1}
        />

        <line
        x1={PAD_L}
        y1={PAD_T}
        x2={PAD_L + plotWidth}
        y2={PAD_T}
        stroke="#ECECEE"
        strokeWidth={1}
        />

        <line
        x1={PAD_L + plotWidth}
        y1={PAD_T}
        x2={PAD_L + plotWidth}
        y2={PAD_T + plotHeight}
        stroke="#ECECEE"
        strokeWidth={1}
        />

        {ptsForLine.length > 1 && lineD && (
          <path
            d={lineD}
            fill="none"
            stroke={DOT_COLOR}
            strokeWidth={1}
            strokeLinejoin="round"
            strokeLinecap="round"
            clipPath="url(#mgClip)"
            opacity={0.25}
          />
        )}

        <DataPoints
          pts={pts}
          activeIndex={activeIndex}
          hoveredIndex={hoveredIndex}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onPointClick={handlePointClick}
        />

        <XAxisLabels
          xLabels={xLabels}
          data={data}
          activeIndex={activeIndex}
          xOf={xOf}
          PAD_T={PAD_T}
          plotHeight={plotHeight}
        />
      </svg>
    </div>
  );
}

export default LineChart;